import React from 'react';
import Carousel from 'react-multi-carousel';

// Preference management functions
const getViewModePreference = (contentType) => {
    try {
        const stored = localStorage.getItem(`storypath_${contentType}_view_mode`);
        return stored && (stored === 'grid' || stored === 'carousel') ? stored : 'grid';
    } catch (error) {
        console.warn('Failed to get view mode preference:', error);
        return 'grid';
    }
};

const setViewModePreference = (contentType, viewMode) => {
    try {
        localStorage.setItem(`storypath_${contentType}_view_mode`, viewMode);
    } catch (error) {
        console.warn('Failed to save view mode preference:', error);
    }
};

// Custom Carousel components (moved from main component)
const CustomCarouselDot = ({ onClick, active }) => (
    <button
        style={{
            background: active ? 'white' : '#86efaccc',
            opacity: active ? 1 : 0.5,
            width: '8px',
            height: '8px',
            margin: '0 5px',
            borderRadius: '50%',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
        }}
        onClick={() => onClick && onClick()}
        aria-label={active ? "Current slide" : "Go to slide"}
    />
);

const CustomLeftArrowComponent = ({ onClick }) => {
    return (
        <button
            onClick={() => onClick()}
            aria-label="Go to previous slide"
            className="absolute left-4 z-10 p-2 transform -translate-y-1/2 bg-black hover:bg-zinc-800 rounded-full top-1/2 bg-opacity-30 hover:bg-opacity-50 focus:outline-none ml-4"
        >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-white">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
        </button>
    );
};

const CustomRightArrowComponent = ({ onClick }) => {
    return (
        <button
            onClick={() => onClick()}
            aria-label="Go to next slide"
            className="absolute right-4 z-10 p-2 transform -translate-y-1/2 bg-black hover:bg-zinc-800 rounded-full top-1/2 bg-opacity-30 hover:bg-opacity-50 focus:outline-none mr-4"
        >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-white">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
        </button>
    );
};

const ViewModeToggle = ({
    data,
    viewMode,
    onViewModeChange,
    contentType,
    GridItemComponent,
    SlideItemComponent,
    dataProperty,
    filterProperty = 'headline',
    keyPrefix = ''
}) => {
    // Handle view mode change with persistence
    const handleViewModeChange = (newMode) => {
        setViewModePreference(contentType, newMode);
        onViewModeChange(newMode);
    };

    // Get the actual data array
    const dataArray = data?.[dataProperty] || [];
    
    // Filter data if filterProperty is provided
    const filteredData = filterProperty 
        ? dataArray.filter(item => item[filterProperty])
        : dataArray;

    // Don't render if no data
    if (!data || !dataArray.length) {
        return null;
    }

    return (
        <>
            {/* View Mode Toggle Buttons */}
            <div className="flex justify-end mb-4 -mt-4 px-6 print:hidden pr-10">
                <div className="text-zinc-300 text-sm mt-5 mr-4">View Mode</div>
                <ul className="relative flex list-none rounded-md bg-black/20 p-3 gap-2" role="list">
                    <li className="flex-auto text-center">
                        <button
                            className={`z-30 flex items-center justify-center w-full px-3 py-2 text-sm font-medium rounded-md cursor-pointer transition-colors duration-150 ease-in-out gap-2 ${viewMode === 'grid'
                                ? 'bg-sky-500 text-white shadow'
                                : 'text-zinc-300 hover:bg-white/5'
                                }`}
                            onClick={() => handleViewModeChange('grid')}
                            role="tab"
                            aria-selected={viewMode === 'grid'}
                        >
                            <img src="/util/app/b3-admin-view-grid.svg" alt="Grid View" className="h-4 w-4" />
                            <span>grid</span>
                        </button>
                    </li>
                    <li className="flex-auto text-center">
                        <button
                            className={`z-30 flex items-center justify-center w-full px-3 py-2 text-sm font-medium rounded-md cursor-pointer transition-colors duration-150 ease-in-out gap-2 ${viewMode === 'carousel'
                                ? 'bg-sky-500 text-white shadow'
                                : 'text-zinc-300 hover:bg-white/5'
                                }`}
                            onClick={() => handleViewModeChange('carousel')}
                            role="tab"
                            aria-selected={viewMode === 'carousel'}
                        >
                            <img src="/util/app/b3-admin-slider-view.svg" alt="Carousel View" className="h-5 w-5" />
                            <span>slider</span>
                        </button>
                    </li>
                </ul>
            </div>

            {/* Content Display */}
            <div className="w-full pr-10">
                {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate__animated animate__fadeIn">
                        {filteredData.map((item, index) => (
                            <GridItemComponent 
                                key={`${keyPrefix}${index}`} 
                                newsItem={item} 
                            />
                        ))}
                    </div>
                ) : (
                    viewMode === 'carousel' && filteredData.length > 0 ? (
                        <Carousel
                            responsive={{
                                desktop: {
                                    breakpoint: { max: 3000, min: 1024 },
                                    items: 1,
                                },
                                tablet: {
                                    breakpoint: { max: 1024, min: 464 },
                                    items: 1,
                                },
                                mobile: {
                                    breakpoint: { max: 464, min: 0 },
                                    items: 1,
                                },
                            }}
                            showDots={true}
                            ssr={true}
                            infinite={true}
                            keyBoardControl={true}
                            containerClass="pb-10 ReachOutCarousel animate__animated animate__fadeIn"
                            dotListClass="custom-dot-list"
                            customDot={<CustomCarouselDot />}
                            customLeftArrow={<CustomLeftArrowComponent />}
                            customRightArrow={<CustomRightArrowComponent />}
                        >
                            {filteredData.map((item, index) => (
                                <div key={`${keyPrefix}slide-${index}`} className="h-full pb-2">
                                    <SlideItemComponent newsItem={item} />
                                </div>
                            ))}
                        </Carousel>
                    ) : null
                )}
            </div>
        </>
    );
};

// Export preference functions for use in main component
export { getViewModePreference, setViewModePreference };
export default React.memo(ViewModeToggle); 